<script>
import ErrorBlock from './ErrorBlock.vue'
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import SmallButton from './SmallButton.vue'
import SliderPrograms from './SliderPrograms.vue'
import MoreProgramsBlock from './MoreProgramsBlock.vue'
import { pluralize } from '@/views/programs/checkup/utils/pluralize.js'

export default {
  components: {
    ErrorBlock,
    SmallButton,
    SliderPrograms,
    MoreProgramsBlock
  },
  emits: [
    'signUpForCheckup',
    'updateSelectedPrograms',
    'reset',
    'openProgramGroupsInfo',
    'updateProgramGroup'
  ],
  props: {
    selectedPrograms: {
      type: Array,
      default: () => []
    },
    programsRegularCheckup: {
      type: Array,
      default: () => []
    },
    programsPersonalCheckup: {
      type: Array,
      default: () => []
    },
    programsSetup: {
      type: Object
    },
    genderId: Number,
    age: Number,
    filialId: Number
  },
  setup (props, { emit }) {
    const { selectedPrograms } = toRefs(props)

    const priceBanner = ref(null)
    const isPriceBannerVisible = ref(false)
    const programRerender = ref(0)

    const totalPrice = ref(0)
    const errorMessage = ref(null)

    const programsSet = new Set()
    const repeatingCodes = []

    const getDiscountObj = (sum) => {
      const hardCodedDisc = [
        {
          min: 95000000000,
          discount: 1
        }
      ]
      // const sortedDiscounts = appStore.appInfo.checkup_discount.toSorted(
      //   (a, b) => a.min - b.min
      // )
      const sortedDiscounts = hardCodedDisc.toSorted((a, b) => a.min - b.min)
      let discountObj = { discount: 0, sum }
      sortedDiscounts.forEach((d, idx, arr) => {
        // если следующий дискаунт подходит для суммы
        const isSmallerThanNext = arr[idx + 1] ? sum < arr[idx + 1].min : true
        if (sum >= d.min && isSmallerThanNext) {
          const factor = 1 - d.discount / 100
          discountObj = { discount: d.discount, sum: sum * factor }
        }
      })

      return discountObj
    }

    const signUpForCheckup = () => {
      if (!selectedPrograms.value.length) {
        errorMessage.value = 'Не выбрана ни одна программа'
        return
      }

      emit('signUpForCheckup')
      emit('updateSelectedPrograms', selectedPrograms.value)
    }

    const numberWithSpaces = (x) => {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }

    const updatePrice = () => {
      const prices = []

      selectedPrograms.value.forEach((pg) => {
        pg.items.forEach((i2) => {
          prices.push(i2.price)
        })
      })

      totalPrice.value = prices.reduce((acc, p) => acc + p, 0)
    }

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        isPriceBannerVisible.value = entry.isIntersecting
      })
    }

    const genderTitle = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.gender_id.find(
          (g) => g.value === props.genderId
        ).title
      } else {
        return ''
      }
    })

    const ageTitle = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.age.find((g) => g.value === props.age).title
      } else {
        return ''
      }
    })

    const filialTitle = computed(() => {
      if (props.programsSetup) {
        return props.programsSetup.filial_id.find(
          (g) => g.value === props.filialId
        ).title
      } else {
        return ''
      }
    })

    const selectedRegularCheckupPrograms = computed(() => {
      if (props.programsRegularCheckup) {
        return props.programsRegularCheckup.filter((group) => group.isSelected)
      } else {
        return []
      }
    })

    const selectedPersonalCheckupPrograms = computed(() => {
      if (props.programsPersonalCheckup) {
        return props.programsPersonalCheckup.filter(
          (group) => group.isSelected
        )
      } else {
        return []
      }
    })

    const programInfoSlider = computed(() => {
      const allPrograms = []
      props.selectedPrograms.forEach((selectedProgram) => {
        selectedProgram.items.forEach((programItem) => {
          allPrograms.push(programItem)
        })
      })

      const mapById = new Map()
      allPrograms.forEach((programItem) => {
        mapById.set(programItem.code, programItem)
      })

      const uniquePrograms = Array.from(mapById.values())

      return uniquePrograms
    })

    watch(
      selectedPrograms,
      () => {
        errorMessage.value = ''
        updatePrice()
      },
      { deep: true }
    )

    onMounted(() => {
      selectedPrograms.value.forEach((pg) => {
        pg.items.forEach((i) => {
          if (!programsSet.has(i.code)) {
            programsSet.add(i.code)
          } else {
            if (!repeatingCodes.includes(i.code)) repeatingCodes.push(i.code)
          }
        })
      })

      updatePrice()

      if (priceBanner.value) {
        const observer = new IntersectionObserver(handleIntersection, {
          root: null,
          threshold: 0
        })
        observer.observe(priceBanner.value)
      }
    })

    function getPluralTitle (typeNumber, num) {
      const type = props.programsSetup.type_id.find(
        (g) => g.value === typeNumber
      )
      if (!type) return ''
      const plurals = type.plural
      return plurals?.[pluralize(num)] || ''
    }

    return {
      signUpForCheckup,
      selectedRegularCheckupPrograms,
      selectedPersonalCheckupPrograms,
      genderTitle,
      filialTitle,
      ageTitle,
      totalPrice,
      programInfoSlider,
      errorMessage,
      getDiscountObj,
      numberWithSpaces,
      isPriceBannerVisible,
      priceBanner,
      programRerender,
      getPluralTitle
    }
  }
}
</script>

<template>
  <div class="program-sign-up__wrapper">
    <div
      :class="{
        'fade-in': !isPriceBannerVisible,
        'fade-out': isPriceBannerVisible,
      }"
      class="program-sign-up__price--fixed"
      ref="priceBanner"
    >
      <div
        v-if="totalPrice !== getDiscountObj(totalPrice).sum"
        class="program-sign-up__price-top"
      >
        <svg class="icon icon-percent">
          <use xlink:href="/uploads/img/sprite.svg#icon-percent"></use>
        </svg>
        <p>
          - {{ getDiscountObj(totalPrice).discount }}% на услуги при заказе
          чекапа
        </p>
      </div>

      <div v-if="totalPrice" class="program-sign-up__price-total">
        <p class="program-sign-up__price-total__discount">
          Общая стоимость:
          {{ numberWithSpaces(getDiscountObj(totalPrice).sum / 100) }} ₽
        </p>
        <p
          class="program-sign-up__price-total__full"
          v-if="totalPrice !== getDiscountObj(totalPrice).sum"
        >
          {{ numberWithSpaces(totalPrice / 100) }} ₽
        </p>
      </div>

      <p>
        Предложение не является офертой, о финальной стоимости вас
        проконсультируют в контакт-центре
      </p>
    </div>

    <h1 class="program-sign-up__title-block">Ваш чекап готов!</h1>

    <div class="program-sign-up">
      <span class="question-select__label">Вы выбрали:</span>
      <div class="program-selected-options">
        <SmallButton size="sm" :disabled="true">{{ genderTitle }}</SmallButton>
        <SmallButton size="sm" :disabled="true">{{ ageTitle }}</SmallButton>
        <SmallButton
          v-if="selectedRegularCheckupPrograms.length"
          @click="
            $emit(
              'openProgramGroupsInfo',
              'open',
              `${selectedRegularCheckupPrograms.length} ${getPluralTitle(
                1,
                selectedRegularCheckupPrograms.length
              )}`,
              selectedRegularCheckupPrograms
            )
          "
          size="sm"
        >
          {{ selectedRegularCheckupPrograms.length }}
          {{ getPluralTitle(1, selectedRegularCheckupPrograms.length) }}
          <svg class="icon icon-arrow-nav">
            <use xlink:href="/uploads/img/sprite.svg#icon-arrow-nav"></use>
          </svg>
        </SmallButton>
        <SmallButton
          @click="
            $emit(
              'openProgramGroupsInfo',
              'open',
              `${selectedPersonalCheckupPrograms.length} ${getPluralTitle(
                2,
                selectedPersonalCheckupPrograms.length
              )}`,
              selectedPersonalCheckupPrograms
            )
          "
          v-if="selectedPersonalCheckupPrograms.length"
          size="sm"
        >
          {{ selectedPersonalCheckupPrograms.length }}
          {{ getPluralTitle(2, selectedPersonalCheckupPrograms.length) }}
          <svg class="icon icon-arrow-nav">
            <use xlink:href="/uploads/img/sprite.svg#icon-arrow-nav"></use>
          </svg>
        </SmallButton>
      </div>

      <span class="question-select__label">Наполнение:</span>
      <SliderPrograms
        :programInfoSlider="programInfoSlider"
        :programsSetup="programsSetup"
        @openProgramGroupsInfo="
          (...args) => $emit('openProgramGroupsInfo', ...args)
        "
      />

      <MoreProgramsBlock
        :programsPersonalCheckup="programsPersonalCheckup"
        @handleProgramGroupUpdate="$emit('updateProgramGroup', $event)"
      />

      <div class="program-sign-up__price" ref="priceBanner">
        <div
          v-if="totalPrice !== getDiscountObj(totalPrice).sum"
          class="program-sign-up__price-top"
        >
          <svg class="icon icon-percent">
            <use xlink:href="/uploads/img/sprite.svg#icon-percent"></use>
          </svg>
          <p>
            - {{ getDiscountObj(totalPrice).discount }}% на услуги при заказе
            чекапа
          </p>
        </div>

        <div v-if="totalPrice" class="program-sign-up__price-total">
          <p class="program-sign-up__price-total__discount">
            Общая стоимость:
            {{ numberWithSpaces(getDiscountObj(totalPrice).sum / 100) }} ₽
          </p>
          <p
            class="program-sign-up__price-total__full"
            v-if="totalPrice !== getDiscountObj(totalPrice).sum"
          >
            {{ numberWithSpaces(totalPrice / 100) }} ₽
          </p>
        </div>

        <div class="program-sign-up__location">
          <p class="location-text">Место проведения:</p>
          <div class="filial-desc">
            <div>
              <svg class="icon icon-map-marker-alt-solid">
                <use
                  xlink:href="/uploads/img/sprite.svg#icon-map-marker-alt-solid"
                ></use>
              </svg>
            </div>
            <p>K+31 {{ filialTitle }}</p>
          </div>
        </div>

        <div class="buttons-wrapper">
          <button
            @click.prevent="$emit('reset')"
            type="button"
            class="program-btn-prev"
            style="display: flex"
          >
            <svg class="program-btn-prev-icon">
              <use xlink:href="/uploads/img/sprite.svg#icon-arrow-nav"></use>
            </svg>
          </button>

          <button
            class="program-selector__reset-btn"
            @click.prevent="signUpForCheckup"
          >
            Записаться на чекап
          </button>
        </div>

        <p class="warning-text">
          Предложение не является офертой, о финальной стоимости вас
          проконсультируют в контакт-центре
        </p>
      </div>

      <ErrorBlock>{{ errorMessage }}</ErrorBlock>
    </div>
  </div>
</template>

<style scoped lang="scss">
.program-selector__reset-btn {
  color: #002856;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.58px;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e1e8f4;
  border-radius: 30px;
  height: 50px;
  width: 100%;
  text-transform: uppercase;
  transition: 0.2s;
  border: none;
}

.program-selector__reset-btn:hover {
  background: #bac7de;
}

.program-sign-up__price--fixed {
  z-index: 100;
  transition: all 0.5s ease;
  width: 500px;
  color: #002856;
  border-radius: 6px;
  background-color: #e4fce6;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  pointer-events: none;
  position: sticky;
  top: calc(100% - 120px);
  margin-left: auto;
  margin-right: 16px;
}
@media (max-width: 550px) {
  .program-sign-up__price--fixed {
    display: none;
  }
}

.fade-in {
  opacity: 1;
}

.fade-out {
  opacity: 0;
}

.program-sign-up__wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 150px;
}

.program-sign-up__title-block {
  color: #002856;
  font-size: 32px;
  line-height: 40px;
  font-weight: 400;
  position: absolute;
}

@media (max-width: 550px) {
  .program-sign-up__title-block {
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    position: relative;
  }
}

.bold-text {
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
.program-sign-up {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.program-sign-up__ready {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 32px;
}
.program-sign-up__ready > img {
  width: 24px;
  height: 24px;
}
.program-sign-up__ready > p {
  margin-top: 2px;
}
.program-sign-up__img {
  margin-bottom: 32px;
}
.program-sign-up__desc {
  margin-bottom: 32px;
}
.program-sign-up__desc > p:first-child {
  text-align: center;
  margin-bottom: 12px;
}
.program-sign-up__desc > p:last-child {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
.programm-sign-up__accordion-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}
.program-sign-up__price {
  color: #002856;
  border-radius: 6px;
  background-color: #e4fce6;
  padding: 16px;
  display: flex;
  flex-direction: column;
  width: 100%;
}

@media (max-width: 575px) {
  .program-sign-up__price {
    position: fixed;
    bottom: 90px;
    left: 0;
    z-index: 1;
    width: 100%;
    border-radius: 0;
  }
}

.program-sign-up__price-top {
  display: flex;
  align-items: center;
}
.program-sign-up__price-top img {
  width: 16px;
  margin-right: 8px;
}
.program-sign-up__price-top p {
  margin-top: 2px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
.program-sign-up__price-total {
  display: flex;
}
.program-sign-up__price-total .program-sign-up__price-total__discount {
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0%;
  margin-bottom: 8px;
}
.program-sign-up__price-total .program-sign-up__price-total__full {
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #7f8da9;
}
.program-sign-up__tip {
  padding: 0 8px;
  text-align: center;
  margin-top: 16px;
  margin-bottom: 16px;
}
.programm-sign-up__accordion-list__btns-wrapper {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.programm-sign-up__accordion-list__btns-wrapper button {
  border: none;
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}
.programm-sign-up__accordion-list__btns-wrapper button:first-child {
  background-color: #bac7de;
}
.programm-sign-up__accordion-list__btns-wrapper button:last-child {
  background-color: #e1e8f4;
}
.program-sign-up__button {
  font-size: 16px;
  font-weight: 400;
  line-height: 23.58px;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #bac7de;
  border-radius: 30px;
  height: 50px;
  width: 100%;
  text-transform: uppercase;
  color: #002856;
  margin-top: 48px;
  transition: 0.2s;
}
.program-sign-up__button:hover {
  background: #c5d0e2;
}

.program-sign-up__button--transparent {
  font-size: 16px;
  font-weight: 400;
  line-height: 23.58px;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 30px;
  height: 50px;
  width: 100%;
  text-transform: uppercase;
  color: #002856;
  margin-top: 16px;
  transition: all 0.2s ease;
  border: 1px solid #002856;
}

.program-sign-up__button--transparent:hover {
  border: 1px solid #7f8da9;
  box-shadow: 0 0 5px 3px rgba(82, 82, 82, 0.05);
}

.icon-percent {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}
.program-selected-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.icon-arrow-nav {
  filter: brightness(0) saturate(100%) invert(15%) sepia(11%) saturate(7169%)
    hue-rotate(179deg) brightness(90%) contrast(98%) !important;
  margin-left: 10px;
  width: 12px !important;
  height: 12px !important;
}
.question-select__label {
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #7f8da9;
  margin-bottom: 8px;
}

.buttons-wrapper {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.icon-arrow-nav-btn {
  transform: rotate(180deg);
  margin-right: 10px;
}
.slick-arrow {
  margin: 0 !important;
  min-width: 50px !important;
  max-height: 50px !important;
  background-color: #eff4fd !important;
  border: none !important;
  cursor: pointer;
}
.filial-desc {
  display: flex;
  gap: 5px;
  color: #8993a6;
  font-weight: 700;
  font-style: Bold;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  svg {
    height: 20px;
    width: 12px;
  }
}

.program-sign-up__location {
  display: flex;
  gap: 10px;
}

@media (max-width: 550px) {
  .program-sign-up__location {
    flex-direction: column;
    gap: 0;
  }
}

.location-text {
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0%;
}
.warning-text {
  text-align: center;
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: #002856;
}

.program-btn-prev {
  background: #e1e8f4;
  width: 50px;
  height: 50px;
  border-radius: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover:not(.disabled) {
    background-color: #bac7de;
  }
  &.disabled {
    background: rgba(#e1e8f4, 0.7);
    opacity: 0.7;
    cursor: auto;
    &:hover {
      color: rgba(#062e4f, 0.7);
    }
  }
}

.program-btn-prev-icon {
  width: 16px;
  height: 16px;
  transform: rotate(180deg);
}

.location-icon {
  width: 12px;
  height: 16px;
}

.modal__overlay {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  background-color: #002856;
  opacity: 0.8;
}
</style>
